// src/componentes/common/ErrorBoundary.jsx
//
// EST1-2: Error Boundary global que evita la pantalla en blanco cuando
// un componente hijo lanza una excepcion durante render o ciclo de vida.
//
// Uso:
//   <ErrorBoundary>
//     <CualquierComponente />
//   </ErrorBoundary>
//
// El boundary captura el error, lo registra en window.__lastErrors[]
// para que la vista de admin (EST2-7) pueda mostrarlos, y renderiza una UI
// amigable con opciones para recargar o volver al inicio.

import React from "react";
import "./ErrorBoundary.css";

// Buffer global de errores para inspeccion posterior (EST2-7).
if (typeof window !== "undefined" && !window.__lastErrors) {
  window.__lastErrors = [];
}

function pushError(entry) {
  if (typeof window === "undefined") return;
  if (!Array.isArray(window.__lastErrors)) window.__lastErrors = [];
  window.__lastErrors.push(entry);
  // Mantener solo los ultimos 100 para no inflar memoria
  if (window.__lastErrors.length > 100) {
    window.__lastErrors.splice(0, window.__lastErrors.length - 100);
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    pushError({
      ts: Date.now(),
      ruta: typeof window !== "undefined" ? window.location.pathname : "",
      mensaje: String(error?.message || error),
      stack: String(error?.stack || ""),
      componentStack: String(errorInfo?.componentStack || ""),
    });
    // Tambien al console para devs
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary capturo:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/app";
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, errorInfo } = this.state;
    const label = this.props.label || "esta seccion";

    return (
      <div className="eb-wrap">
        <div className="eb-card">
          <div className="eb-icon">⚠️</div>
          <h2 className="eb-title">Ups, {label} tuvo un problema</h2>
          <p className="eb-desc">
            La aplicacion atrapo un error inesperado y lo guardo para revision.
            Tu informacion no se perdio. Intenta recargar la pagina o vuelve al
            inicio para continuar.
          </p>

          <div className="eb-actions">
            <button className="eb-btn eb-btn-primary" onClick={this.handleReload}>
              🔄 Recargar pagina
            </button>
            <button className="eb-btn eb-btn-secondary" onClick={this.handleHome}>
              🏠 Volver al inicio
            </button>
            <button className="eb-btn eb-btn-light" onClick={this.handleReset}>
              Reintentar
            </button>
          </div>

          <details className="eb-details">
            <summary>Detalle tecnico (solo para soporte)</summary>
            <pre className="eb-stack">{String(error?.message || error)}</pre>
            {errorInfo?.componentStack && (
              <pre className="eb-stack-soft">{errorInfo.componentStack}</pre>
            )}
          </details>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
